import {
  $createTextNode,
  $getRoot,
  $isTextNode,
  ElementNode,
  LexicalNode,
} from "lexical";
import {
  $createHtmlNode,
  $isHtmlNode,
} from "src/components/DocViewer/components/markdownDocViewer/rawEditor/HtmlNode";

type ContentPart = { type: "text" | "span"; content: string };

interface SplitSpanInfo {
  openingNodeIndex: number;
  closingNodeIndex: number;
  openingTag: string;
}

/**
 * Post-processes HTML span tags in Lexical nodes to ensure they are properly structured.
 * Handles two cases:
 * 1. Span tags split across multiple nodes (opening tag in one, closing tag in another)
 * 2. Complete span tags within single nodes that need to be extracted
 */
export const postProcessHtmlTagsInNodes = (
  root: ReturnType<typeof $getRoot>
): void => {
  let changesMade = true;
  while (changesMade) {
    changesMade = false;
    const nodes = collectAllNodes(root);

    if (processSplitSpanTags(nodes)) {
      changesMade = true;
    }

    const nodesAfterSplitProcessing = collectAllNodes(root);
    for (const node of nodesAfterSplitProcessing.filter(
      checkIfContainsSpanTag
    )) {
      if (splitNodeIntoSpanTags(node)) {
        changesMade = true;
      }
    }
  }
};

const collectAllNodes = (node: LexicalNode): LexicalNode[] => {
  const nodes: LexicalNode[] = [];
  if ($isTextNode(node) || $isHtmlNode(node)) {
    nodes.push(node);
  }
  if (node instanceof ElementNode) {
    node.getChildren().forEach((child) => {
      nodes.push(...collectAllNodes(child));
    });
  }
  return nodes;
};

const processSplitSpanTags = (nodes: LexicalNode[]): boolean => {
  const splitSpans: SplitSpanInfo[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const content = getNodeContent(nodes[i]);
    if (!content) continue;

    const openingSpanMatch = content.match(/<span(?:\s+[^>]*)?>/i);
    if (!openingSpanMatch) continue;

    const openingTag = openingSpanMatch[0];
    const openingTagEndIndex = openingSpanMatch.index! + openingTag.length;
    const closingTagIndexInSameNode = content.indexOf(
      "</span>",
      openingTagEndIndex
    );

    const hasClosingTagInSameNode = closingTagIndexInSameNode !== -1;
    if (!hasClosingTagInSameNode) {
      for (let j = i + 1; j < nodes.length; j++) {
        if (getNodeContent(nodes[j])?.includes("</span>")) {
          splitSpans.push({
            openingNodeIndex: i,
            closingNodeIndex: j,
            openingTag,
          });
          break;
        }
      }
    }
  }

  // Process in reverse order to maintain indices when modifying nodes
  const splitSpansInReverseOrder = splitSpans.reverse();
  return splitSpansInReverseOrder.some((splitSpan) =>
    handleSplitSpan(nodes, splitSpan)
  );
};

const replaceNodeContent = (node: LexicalNode, newContent: string): void => {
  node.replace($createHtmlNode(newContent));
};

const handleSplitSpan = (
  nodes: LexicalNode[],
  splitSpan: SplitSpanInfo
): boolean => {
  const { openingNodeIndex, closingNodeIndex, openingTag } = splitSpan;

  const isValidIndex = (index: number) => index < nodes.length;
  const isNodeAttached = (index: number) => nodes[index].isAttached();
  const canProcessSplitSpan =
    isValidIndex(openingNodeIndex) &&
    isValidIndex(closingNodeIndex) &&
    isNodeAttached(openingNodeIndex) &&
    isNodeAttached(closingNodeIndex);

  if (!canProcessSplitSpan) {
    return false;
  }

  const openingNode = nodes[openingNodeIndex];
  const closingNode = nodes[closingNodeIndex];

  const openingNodeContent = getNodeContent(openingNode);
  replaceNodeContent(openingNode, openingNodeContent + "</span>");

  const closingNodeContent = getNodeContent(closingNode);
  replaceNodeContent(closingNode, openingTag + closingNodeContent);

  for (let i = openingNodeIndex + 1; i < closingNodeIndex; i++) {
    const intermediateNode = nodes[i];
    if (intermediateNode.isAttached()) {
      const intermediateNodeContent = getNodeContent(intermediateNode);
      replaceNodeContent(
        intermediateNode,
        openingTag + intermediateNodeContent + "</span>"
      );
    }
  }

  return true;
};

const getNodeContent = (node: LexicalNode): string => {
  if ($isTextNode(node)) {
    return node.getTextContent();
  }
  if ($isHtmlNode(node)) {
    return node.getHtml();
  }
  return "";
};

const hasCompleteSpanTags = (content: string): boolean => {
  return Boolean(content && /<span[^>]*>.*?<\/span>/gi.test(content));
};

const parseHtmlSpanTags = (content: string): ContentPart[] => {
  if (!content) {
    return [{ type: "text", content }];
  }

  const spanRegex = /<span[^>]*>.*?<\/span>/gis;
  const spanMatches = Array.from(content.matchAll(spanRegex));

  if (spanMatches.length === 0) {
    return [{ type: "text", content }];
  }

  const parts: ContentPart[] = [];
  let lastIndex = 0;

  for (const match of spanMatches) {
    const matchIndex = match.index ?? 0;
    const matchLength = match[0].length;

    const hasTextBeforeSpan = matchIndex > lastIndex;
    if (hasTextBeforeSpan) {
      const textBeforeSpan = content.substring(lastIndex, matchIndex);
      if (textBeforeSpan) {
        parts.push({ type: "text", content: textBeforeSpan });
      }
    }

    const spanContent = match[0];
    parts.push({ type: "span", content: spanContent });
    lastIndex = matchIndex + matchLength;
  }

  const hasTextAfterLastSpan = lastIndex < content.length;
  if (hasTextAfterLastSpan) {
    const textAfterLastSpan = content.substring(lastIndex);
    if (textAfterLastSpan) {
      parts.push({ type: "text", content: textAfterLastSpan });
    }
  }

  return parts;
};

const createNodesFromParts = (
  parts: ContentPart[],
  isOriginalHtmlNode: boolean
): LexicalNode[] => {
  const nodes: LexicalNode[] = [];

  for (const part of parts) {
    if (part.type === "span") {
      nodes.push($createHtmlNode(part.content));
      continue;
    }

    if (part.type === "text" && part.content.length === 0) {
      continue;
    }

    const textContent = part.content;
    const containsHtml =
      hasCompleteSpanTags(textContent) || /<[^>]+>/.test(textContent);
    if (containsHtml) {
      nodes.push($createHtmlNode(textContent));
    } else if (isOriginalHtmlNode) {
      const escapedHtmlContent = textContent
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      nodes.push($createHtmlNode(escapedHtmlContent));
    } else {
      nodes.push($createTextNode(textContent));
    }
  }

  return nodes;
};

const replaceNodeWithNodes = (
  node: LexicalNode,
  nodesToInsert: LexicalNode[]
): void => {
  if (nodesToInsert.length === 0) {
    return;
  }

  const firstNode = nodesToInsert[0];
  node.replace(firstNode);

  let previousNode = firstNode;
  for (let i = 1; i < nodesToInsert.length; i++) {
    previousNode.insertAfter(nodesToInsert[i]);
    previousNode = nodesToInsert[i];
  }
};

const checkIfContainsSpanTag = (node: LexicalNode): boolean => {
  if (!node.isAttached()) {
    return false;
  }
  const content = getNodeContent(node);
  return hasCompleteSpanTags(content);
};

const handleNodeWithSingleSpanTag = (
  node: LexicalNode,
  spanContent: string
): boolean => {
  const isAlreadyCorrectHtmlNode =
    $isHtmlNode(node) && node.getHtml() === spanContent;
  const hasNoParent = !node.getParent();
  const shouldSkipNode = isAlreadyCorrectHtmlNode || hasNoParent;

  if (shouldSkipNode) {
    return false;
  }

  node.replace($createHtmlNode(spanContent));
  return true;
};

const splitMultiplePartsNode = (
  node: LexicalNode,
  parts: ContentPart[]
): boolean => {
  if (parts.length <= 1 || !node.getParent()) {
    return false;
  }

  const nodesToInsert = createNodesFromParts(parts, $isHtmlNode(node));
  if (nodesToInsert.length === 0) {
    return false;
  }

  replaceNodeWithNodes(node, nodesToInsert);
  return true;
};
const splitNodeIntoSpanTags = (node: LexicalNode): boolean => {
  const content = getNodeContent(node);
  const parts = parseHtmlSpanTags(content);

  if (parts.length === 1 && parts[0].type === "span") {
    return handleNodeWithSingleSpanTag(node, parts[0].content);
  }

  return splitMultiplePartsNode(node, parts);
};
